import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Paperclip, Send, Lock, Globe, ChevronDown } from 'lucide-react';

/**
 * Recipient dropdown (teacher/admin only). For students it's not rendered.
 */
function RecipientSelector({ students, recipientId, setRecipientId, displayName }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="relative" ref={menuRef}>
      <button
        data-testid="recipient-selector-btn"
        onClick={() => setOpen(v => !v)}
        className={`flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
          recipientId
            ? 'bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100'
            : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
        }`}
      >
        {recipientId ? <Lock className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
        <span>Para: <strong>{displayName}</strong></span>
        <ChevronDown className="w-3 h-3" />
      </button>

      {open && (
        <div className="absolute bottom-full mb-2 left-0 bg-white border rounded-xl shadow-lg z-50 min-w-[220px] overflow-hidden">
          <button
            data-testid="recipient-all-btn"
            onClick={() => { setRecipientId(''); setOpen(false); }}
            className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-slate-50 ${!recipientId ? 'font-bold text-blue-600' : 'text-slate-700'}`}
          >
            <Globe className="w-4 h-4 text-slate-400" />
            Todos los estudiantes
          </button>
          <div className="border-t" />
          {students.map(s => {
            const sid = s.id || s.user_id;
            return (
              <button
                key={sid}
                data-testid={`recipient-student-${sid}`}
                onClick={() => { setRecipientId(sid); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-slate-50 ${recipientId === sid ? 'font-bold text-amber-700 bg-amber-50' : 'text-slate-700'}`}
              >
                <Lock className="w-4 h-4 text-amber-400" />
                {s.first_name} {s.last_name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function CommentInputBar({
  text, setText, file, setFile, sending, onSend,
  isTeacherOrAdmin, students, recipientId, setRecipientId, recipientDisplayName,
  placeholder
}) {
  const fileRef = useRef(null);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <div className="border-t bg-white p-4 rounded-b-xl space-y-2">
      {isTeacherOrAdmin && students.length > 0 && (
        <RecipientSelector
          students={students}
          recipientId={recipientId}
          setRecipientId={setRecipientId}
          displayName={recipientDisplayName}
        />
      )}

      {file && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg text-sm text-blue-700">
          <Paperclip className="w-3 h-3" />
          <span className="truncate">{file.name}</span>
          <button
            onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ''; }}
            className="text-blue-400 hover:text-blue-600 ml-auto"
          >&times;</button>
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          onClick={() => fileRef.current?.click()}
          className="p-2 text-slate-400 hover:text-blue-500 transition-colors"
          data-testid="attach-file-btn"
        >
          <Paperclip className="w-5 h-5" />
        </button>
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          onChange={e => setFile(e.target.files?.[0])}
        />
        <Input
          data-testid="comment-input"
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`flex-1 rounded-xl ${recipientId ? 'border-amber-300 focus:ring-amber-300' : ''}`}
        />
        <Button
          data-testid="send-comment-btn"
          onClick={onSend}
          disabled={sending || (!text.trim() && !file)}
          size="sm"
          className="rounded-xl px-4 gap-1"
        >
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
